import {
  Chip,
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useUnsafeTranslation } from '../../i18next';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
  },
  inputContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start', // Changed from 'center' to 'flex-start' for top alignment
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    minHeight: 40,
    cursor: 'text',
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
  chip: {
    height: 24,
    fontSize: '0.75rem',
    color: theme.palette.common.black,
    //backgroundColor: theme.palette.grey[200],
    '& .MuiChip-deleteIcon': {
      color: theme.palette.common.black,
      '&:hover': {
        color: theme.palette.primary.contrastText,
      },
    },
  },
  blankChip: {
    height: 24,
    fontSize: '0.75rem',
    fontStyle: 'italic',
    fontWeight: 'normal',
    backgroundColor: theme.palette.grey[600],
    color: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[800]}`,
    '& .MuiChip-label': {
      fontStyle: 'italic',
      textTransform: 'lowercase',
    },
    '& .MuiChip-deleteIcon': {
      color: theme.palette.common.white,
    },
    '&:hover': {
      backgroundColor: theme.palette.grey[900], // Darker on hover instead of white
      '& .MuiChip-deleteIcon': {
        color: theme.palette.common.white,
      },
    },
  },
  // hiddenInput: {
  //   border: 'none',
  //   outline: 'none',
  //   flexGrow: 1,
  //   flexShrink: 1,
  //   flexBasis: '1%',
  //   fontSize: '1rem',
  //   fontWeight: 200,
  //   fontFamily: theme.typography.fontFamily,
  //   backgroundColor: 'transparent',
  //   marginTop: '0',
  //   // Hide the input when not focused
  //   width: 0,
  //   minWidth: 0,
  //   opacity: 0,
  //   transition: theme.transitions.create(['width', 'opacity', 'min-width'], {
  //     duration: theme.transitions.duration.short,
  //   }),
  //   '&::placeholder': {
  //     color: theme.palette.text.secondary,
  //   },
  // },
  // hiddenInputFocused: {
  //   // Show and expand the input when focused
  //   width: 'auto',
  //   minWidth: 120,
  //   opacity: 1,
  //   flexBasis: '100%', // Force new line when focused
  // },
  hiddenInputAlternative: {
    border: 'none',
    outline: 'none',
    fontSize: '1rem',
    fontWeight: 200,
    fontFamily: theme.typography.fontFamily,
    backgroundColor: 'transparent',
    marginTop: '0',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: '1%',
    minWidth: '120px',
    // Use visibility and height instead of width/opacity
    height: '1.2em', // Normal text height
    '&::placeholder': {
      color: theme.palette.text.secondary,
    },
  },
  hiddenInputAlternativeFocused: {
    flexBasis: '100%',
  },
  inputWrapper: {
    display: 'flex',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: '1%',
    minWidth: '120px',
  },
  hiddenInputStable: {
    border: 'none',
    outline: 'none',
    fontSize: '1rem',
    fontWeight: 200,
    fontFamily: theme.typography.fontFamily,
    backgroundColor: 'transparent',
    marginTop: '0',
    width: '100%',
    '&::placeholder': {
      color: theme.palette.text.secondary,
    },
  },
  suggestionsPopper: {
    zIndex: theme.zIndex.modal
  },
  suggestionTitle: {
    fontWeight: theme.typography.fontWeightBold
  },
  suggestionTip: {
    paddingLeft: theme.spacing(1.25),
    paddingTop: theme.spacing(1.25)
  },
  suggestionsPaper: {
    maxHeight: 200,
    overflow: 'auto',
  },
  suggestionChip: {
    margin: theme.spacing(0.25),
    height: 24,
    fontSize: '0.75rem',
  },
  blankSuggestionChip: {
    margin: theme.spacing(0.25),
    height: 24,
    fontSize: '0.75rem',
    fontStyle: 'italic',
    backgroundColor: theme.palette.grey[600],
    color: theme.palette.common.white,
    '& .MuiChip-label': {
      fontStyle: 'italic',
      textTransform: 'lowercase',
    },
  },
  suggestionsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(1),
  },
}));

export interface MultiChipInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  options?: string[] | (() => Promise<string[]>);
  placeholder?: string;
  disabled?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  fullWidth?: boolean;
  label?: string;
  allowBlankOption?: boolean;
}

const BLANK_VALUE = '__BLANK__';
const BLANK_DISPLAY = 'blank';

export const MultiChipInput: React.FC<MultiChipInputProps> = ({
  value = [],
  onChange,
  options = [],
  placeholder = '',
  disabled = false,
  variant = 'outlined',
  fullWidth = false,
  label,
  allowBlankOption = true,
}) => {
  const { t } = useUnsafeTranslation();

  const classes = useStyles();
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [opts, setOpts] = useState<string[] | null>(
    typeof options === 'function' ? null : options
  );
  const [inputFocused, setInputFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isAsync = typeof options === 'function';

  // Load async options when suggestions are opened
  useEffect(() => {
    if (showSuggestions && opts === null && isAsync) {
      setLoading(true);
      (options as () => Promise<string[]>)()
        .then(setOpts)
        .finally(() => setLoading(false));
    }
  }, [showSuggestions, opts, options, isAsync]);

  // Update options when they change (for sync options)
  useEffect(() => {
    if (!isAsync) {
      setOpts(options as string[]);
    }
  }, [options, isAsync]);

  // Filter suggestions based on input value and exclude already selected values
  useEffect(() => {
    if (!opts) {
      setFilteredSuggestions([]);
      return;
    }

    let allSuggestions = [...opts];
    
    // Add blank option FIRST if allowed and not already selected
    if (allowBlankOption && !value.includes(BLANK_VALUE)) {
      allSuggestions = [BLANK_VALUE, ...allSuggestions];
    }

    if (inputValue.trim()) {
      const filtered = allSuggestions.filter((suggestion) => {
        if (suggestion === BLANK_VALUE) {
          // Check if input matches "blank" or partial matches like "b", "bl", "bla"
          return BLANK_DISPLAY.startsWith(inputValue.toLowerCase());
        }
        return suggestion.toLowerCase().includes(inputValue.toLowerCase()) &&
               !value.includes(suggestion);
      });
      setFilteredSuggestions(filtered);
    } else {
      // Show all unselected suggestions when no input
      setFilteredSuggestions(allSuggestions.filter(s => !value.includes(s)));
    }
  }, [inputValue, opts, value, allowBlankOption]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    // Show suggestions if we have options or if blank option is allowed
    const hasOptions = (opts && opts.length > 0) || allowBlankOption;
    setShowSuggestions(hasOptions);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && inputValue.trim()) {
      event.preventDefault();
      addValue(inputValue.trim());
    } else if (event.key === 'Backspace' && !inputValue && value.length > 0) {
      // Remove last chip if backspace on empty input
      removeValue(value[value.length - 1]);
    } else if (event.key === 'Escape') {
      // Clear input on escape
      setInputValue('');
      setShowSuggestions(false);
    } else if (event.key === 'Tab' && inputValue.trim()) {
      // Add value on tab if there's input
      addValue(inputValue.trim());
    }
  };

  const addValue = (newValue: string) => {
    // Handle special case for blank
    if (allowBlankOption && newValue.toLowerCase() === BLANK_DISPLAY) {
      newValue = BLANK_VALUE;
    }
    
    if (newValue && !value.includes(newValue)) {
      onChange([...value, newValue]);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  const removeValue = (valueToRemove: string) => {
    console.log('remove', valueToRemove);
    onChange(value.filter((v) => v !== valueToRemove));
  };

  const handleSuggestionMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
};

  const handleSuggestionClick = (suggestion: string, event?: React.MouseEvent) => {
    event?.stopPropagation();
    console.log(suggestion);
    addValue(suggestion === BLANK_VALUE ? BLANK_DISPLAY : suggestion);

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleContainerClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
      // Show suggestions if we have them or if blank option is allowed
      const hasOptions = (opts && opts.length > 0) || allowBlankOption;
      setShowSuggestions(hasOptions);
    }
  };

  const handleClickAway = () => {
    // Add the current input value as a chip if it's not empty
    if (inputValue.trim()) {
      addValue(inputValue.trim());
    }
    setShowSuggestions(false);
    setInputFocused(false);
  };

  const handleInputFocus = () => {
    setInputFocused(true);
    const hasOptions = (opts && opts.length > 0) || allowBlankOption;
    setShowSuggestions(hasOptions);
  };

  const handleInputBlur = () => {
    setInputFocused(false);
  };

  const renderChip = (val: string) => {
    const isBlank = val === BLANK_VALUE;
    return (
      <Chip
        key={val}
        label={isBlank ? BLANK_DISPLAY : val}
        onDelete={() => removeValue(val)}
        deleteIcon={<X />}
        className={isBlank ? classes.blankChip : classes.chip}
        size="small"
        color={isBlank ? undefined : "default"}
        disabled={disabled}
      />
    );
  };

  const renderSuggestionChip = (suggestion: string) => {
    const isBlank = suggestion === BLANK_VALUE;
    return (
      <Chip
        key={suggestion}
        label={isBlank ? BLANK_DISPLAY : suggestion}
        onClick={(event) => handleSuggestionClick(suggestion, event)}
        onMouseDown={handleSuggestionMouseDown}
        className={isBlank ? classes.blankSuggestionChip : classes.suggestionChip}
        color="default"
        clickable
      />
    );
  };

  const hasSuggestions = (opts && opts.length > 0) || allowBlankOption;
  const shouldShowSuggestions = showSuggestions && hasSuggestions && (filteredSuggestions.length > 0 || loading);

  // For outlined variant, we'll use a custom container
  if (variant === 'outlined') {
    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        <div className={classes.root} ref={containerRef} style={{ width: fullWidth ? '100%' : 'auto' }}>
          {label && (
            <label style={{ 
              fontSize: '0.75rem', 
              color: '#666', 
              marginBottom: 4, 
              display: 'block' 
            }}>
              {label}
            </label>
          )}
          <div
            className={classes.inputContainer}
            onClick={handleContainerClick}
          >
            {value.map(renderChip)}
            <div className={classes.inputWrapper}>
              <input
                ref={inputRef}
                className={`${classes.hiddenInputStable} ${inputFocused ? classes.hiddenInputAlternativeFocused : ''}`}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}              
                placeholder={value.length === 0 ? placeholder : ''}
                disabled={disabled}
              />
            </div>
          </div>
          <Popper
            open={shouldShowSuggestions}
            anchorEl={containerRef.current}
            className={classes.suggestionsPopper}
            placement="bottom-start"
            style={{ width: containerRef.current?.offsetWidth }}
            modifiers={{
              flip: {
                enabled: true,
              },
              preventOverflow: {
                enabled: true,
                boundariesElement: 'viewport',
              },
            }}
          >
            <Paper className={classes.suggestionsPaper}>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                  <CircularProgress size={20} />
                </div>
              ) : (
                <>
                  <div className={classes.suggestionTip}>
                    <Typography className={classes.suggestionTitle}>
                      {t('Suggestions')}
                    </Typography>
                    <Typography>
                      {t('Or type to add new option')}
                    </Typography>
                  </div>
                  
                  <div className={classes.suggestionsContainer}>
                    {filteredSuggestions.map(renderSuggestionChip)}
                  </div>
                </>
              )}
            </Paper>
          </Popper>
        </div>
      </ClickAwayListener>
    );
  }

  // For other variants, fallback to TextField with custom input
  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div className={classes.root} ref={containerRef}>
        <TextField
          fullWidth={fullWidth}
          variant={variant}
          label={label}
          disabled={disabled}
          onClick={handleContainerClick}
          InputProps={{
            startAdornment: value.length > 0 ? (
              <InputAdornment position="start">
                {value.map(renderChip)}
              </InputAdornment>
            ) : undefined,
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : undefined,
          }}
          inputProps={{
            value: inputValue,
            onChange: handleInputChange,
            onKeyDown: handleInputKeyDown,
            onFocus: handleInputFocus,
            placeholder: value.length === 0 ? placeholder : '',
          }}
        />
        <Popper
          open={shouldShowSuggestions}
          anchorEl={containerRef.current}
          className={classes.suggestionsPopper}
          placement="bottom-start"
          style={{ width: containerRef.current?.offsetWidth }}
        >
          <Paper className={classes.suggestionsPaper}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                <CircularProgress size={20} />
              </div>
            ) : (
              <div className={classes.suggestionsContainer}>
                {filteredSuggestions.map(renderSuggestionChip)}
              </div>
            )}
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};